import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

const CATEGORY_QUERY_PARAM_NAMES = [
  'pub_categoryLevel1',
  'pub_categoryLevel2',
  'pub_categoryLevel3',
];

const getPaginationPageParams = (search, listingTypeParam) => {
  const categoryValues = CATEGORY_QUERY_PARAM_NAMES.map(paramName => search?.[paramName]).filter(
    Boolean
  );
  const pageSearchParams = Object.entries(search || {}).reduce((picked, entry) => {
    const [key, value] = entry;
    return CATEGORY_QUERY_PARAM_NAMES.includes(key) ? picked : { ...picked, [key]: value };
  }, {});

  return categoryValues.length > 1
    ? {
        pageName:
          categoryValues.length > 2 ? 'SearchPageWithCategoryLevel3' : 'SearchPageWithCategory',
        pagePathParams: {
          categoryLevel1: categoryValues[0],
          categoryLevel2: categoryValues[1],
          categoryLevel3: categoryValues[2],
        },
        pageSearchParams,
      }
    : categoryValues.length === 1
    ? {
        pageName: 'SearchPageWithListingType',
        pagePathParams: { listingType: categoryValues[0] },
        pageSearchParams,
      }
    : {
        pageName: listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage',
        pagePathParams: { listingType: listingTypeParam },
        pageSearchParams,
      };
};

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const { pageName, pagePathParams, pageSearchParams } = getPaginationPageParams(
    search,
    listingTypeParam
  );

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={pagePathParams}
        pageSearchParams={pageSearchParams}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListingCard
              className={css.listingCard}
              listing={l}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
              showAuthorInfo={false}
              showSearchMetadata={true}
              showVerifiedTooltip={true}
            />
          </li>
        ))}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
